import { z } from "zod";
import type { BusinessNotificationType } from "./business-notification-catalog";

/**
 * In-app notifications — the notification centre behind the header bell.
 *
 * Stored per business, addressed to one member:
 *   Businesses/{businessId}/notifications/{notificationId}
 *
 * Written by the same producers that send the emails (today: the morning
 * "att uppmärksamma" digest), so a member who has the in-app channel on but
 * email off still sees the report. Delivery is per recipient — one document
 * each — rather than one shared document with a read-by map, so a query for
 * "my unread" needs no fan-out read and no composite index beyond
 * (recipientUid, createdAt).
 */

export interface BusinessNotificationRecord {
  id: string;
  businessId: string;
  /** Firebase uid of the member this notification is addressed to. */
  recipientUid: string;
  type: BusinessNotificationType;
  title: string;
  body: string;
  /** In-app path to open, e.g. "/" or "/coach". Never an external URL. */
  link: string | null;
  createdAt: string | null;
  readAt: string | null;
}

/**
 * Deterministic id so a producer that retries — Trigger.dev tasks retry up to
 * three times — cannot deliver the same notification twice. Mirrors the
 * `insights-digest:{businessId}:{day}` idempotency key already used on the
 * task itself.
 */
export function makeBusinessNotificationId(
  recipientUid: string,
  type: BusinessNotificationType,
  dayStamp: string,
): string {
  return `${recipientUid}_${type}_${dayStamp}`;
}

export const ListBusinessNotificationsInput = z.object({
  businessId: z.string().min(1),
  limit: z.number().int().min(1).max(50).default(20),
  /** Only unread — what the bell badge counts. */
  unreadOnly: z.boolean().default(false),
});
export type ListBusinessNotificationsInput = z.infer<
  typeof ListBusinessNotificationsInput
>;

export const MarkBusinessNotificationReadInput = z.object({
  businessId: z.string().min(1),
  notificationId: z.string().min(1),
});
export type MarkBusinessNotificationReadInput = z.infer<
  typeof MarkBusinessNotificationReadInput
>;

export const MarkAllBusinessNotificationsReadInput = z.object({
  businessId: z.string().min(1),
});
export type MarkAllBusinessNotificationsReadInput = z.infer<
  typeof MarkAllBusinessNotificationsReadInput
>;
